import * as THREE from 'three';
import CameraControls from 'camera-controls';
import Camera from './Camera.js';
import Time from './utils/Time.js'
import Sizes from './utils/Sizes.js';
import Renderer from './Renderer.js';
import World from './world/World.js'
import Resources from './utils/Resources.js'
// import Debug from './utils/Debug.js'
import sources from './sources.js'

// Vite
// import { LOCALE } from '../../vite.config.js';

// Features
import Hotspots from "../features/hotspots.js"
import Teleports from "../features/Teleports.js"
// import { points, locations } from '../features/Hotspots.js'
import CameraTransition from '../features/CameraTransition.js'
import Raycaster from '../features/Raycaster.js'
import ChangeColor from '../features/ChangeColor.js';
import ChangeAnimation from '../features/ChangeAnimation.js';
import TrashManager from '../features/TrashManager.js'
// import { trashbags } from '../features/TrashManager.js'
import LodManager from '../features/LodManager.js'
import Menu from '../features/Menu.js'
import LoadingScreen from '../features/LoadingScreen.js'

CameraControls.install({ THREE: THREE });

let instance = null; // Keep this for Singleton pattern

export default class Experience {
    constructor(canvas) {
        if (instance) {
            return instance; // If the instance already exists, return the existing instance
        }

        instance = this;

        // Flag mobile use globally
        globalThis.mobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        // Global access
        window.experience = this;

        // Options
        this.canvas = canvas;

        // Setup
        // this.debug = new Debug();
        this.sizes = new Sizes();
        this.time = new Time();
        this.scene = new THREE.Scene();
        this.resources = new Resources(sources);
        this.hotspots = new Hotspots(this.scene, this, this.resources, this.cameraControls); // Before the camera so we can set the initial location based on URL.
        this.camera = new Camera();
        this.renderer = new Renderer();
        // Pass a callback to the World constructor
        this.world = new World((fish) => {
            // The callback is executed when the resources are ready
            // this.changeAnimation = new ChangeAnimation(cube, this.experience, this.renderer, this.camera.instance, this.raycaster, this.sizes);
            this.changeAnimation = new ChangeAnimation(this.world, fish, this.experience, this.renderer, this.camera.instance, this.raycaster, this.sizes, this.time, this.cameraControls, this.resources);
            this.changeColor = new ChangeColor(this.renderer, this.camera.instance, this.raycaster, this.sizes);
        });
        this.lodManager = new LodManager(this.scene, this.cameraControls, this.resources, this.world);
        this.frustum = new THREE.Frustum(); // Necessary for hotspots and trashbags
        this.cameraViewProjectionMatrix = new THREE.Matrix4(); // Necessary for hotspots and trashbags
        this.cameraControls = new CameraControls(this.camera.instance, this.canvas);
        this.raycaster = new Raycaster();

        this.teleports = new Teleports(this.scene, this, this.raycaster, this.resources, this.cameraControls, this.camera, this.renderer); // Needs to happen before transition? But after raycast
        this.trashbags = new TrashManager(this.hotspots, this.experience, this.resources, this.teleports);

        if (this.resources.on('ready', () => {
            // Load Menu interactions
            this.menu = new Menu(this.teleports)

            // Can't get the pushed data from the placeholders of the Blender model if the model hasn't been loaded, hence "ready".
            this.transition = new CameraTransition(this.experience, this.scene, this.camera.instance, this.cameraControls, this.hotspots, this.hotspots.points, this.hotspots.locations, this.placeholderData, this.teleports, this.time.delta, this.renderer, this.sizes, this.time, this.raycaster, this.resources, this.frustum, this.cameraViewProjectionMatrix);

            // Can't load before this.transition
            this.loadingScreen = new LoadingScreen(this.resources, this.transition, this.teleports, this.cameraControls)
            // LOD Setup
            // Register your flowers
            // this.lodManager.register({
            //     name: 'flowerHigh',
            //     type: 'object', // Type of swap we want
            //     lowObject: this.world.flowers.resource,
            //     lowInstances: this.world.flowers.instancedMesh, // Need to hide the object but also the instances
            //     highUrl: '/models/Flowers/glTF/FlowerSimple.glb',
            //     position: new THREE.Vector3(0, 0, 0),
            //     threshold: 10
            // });

            this.lodManager.register({
                name: 'treeHigh',
                type: 'object', // Type of swap we want
                lowObject: this.world.tree.resource,
                lowInstances: this.world.tree.foliageInstancedMesh, // Need to hide the object but also the instances
                highUrl: '/models/Tree/glTF/Tree.glb',
                position: new THREE.Vector3(-1.5, .615, -5.3),
                threshold: 6
            });

            this.lodManager.register({
                name: 'poplarHigh',
                type: 'object', // Type of swap we want
                lowObject: this.world.poplar.resource,
                lowInstances: this.world.poplar.foliageInstancedMesh, // Need to hide the object but also the instances
                highUrl: '/models/Poplar/glTF/Poplar.glb',
                position: new THREE.Vector3(-1.5, .615, -5.3),
                threshold: 6
            });

            this.lodManager.register({
                name: 'wallsHigh',
                type: 'texture',
                object: this.world.walls.resource,
                lowTex: '/models/Walls/glTF/Textures/textureSimple.jpg',
                highTex: `/models/Walls/glTF/Textures/texture_${__LOCALE__}.webp`,
                position: new THREE.Vector3(3.34, .325, 1.15),
                threshold: 1
            });

            this.lodManager.register({
                name: 'wallsOuterHigh',
                type: 'texture',
                object: this.world.wallsOuter.resource,
                lowTex: '/models/WallsOuter/glTF/Textures/textureSimple.jpg',
                highTex: `/models/WallsOuter/glTF/Textures/texture_${__LOCALE__}.webp`,
                position: new THREE.Vector3(3.34, .325, 1.15),
                threshold: 1
            });

        }))

            // this.changeAnimation = new ChangeAnimation(this.world.cube, this.experience, this.renderer, this.camera.instance);
            // this.changeColor = new ChangeColor(this.world.cube, this.renderer, this.camera.instance, this.raycaster, this.sizes);

            // Global variables for debugging and access
            globalThis.THREE = THREE;
        globalThis.camera = this.camera;
        globalThis.cameraControls = this.cameraControls;
        globalThis.scene = this.scene;
        globalThis.renderer = this.renderer;
        globalThis.instance = instance;
        globalThis.canvas = this.canvas;

        // Sizes resize event
        this.sizes.on('resize', () => {
            this.resize();
        }); // Listen to "resize" that is triggered in sizes.js

        // Time tick event
        this.time.on('tick', () => {
            this.update();
        });

        // No difference
        // const frameInterval = 1000 / 120
        // let clock = new THREE.Clock();
        // let delta = 0;

        // this.time.on('tick', () => {
        //     delta += clock.getDelta();

        //     if (delta > frameInterval) {
        //     this.update();

        //         delta = delta % frameInterval
        //     }
        // });
    }

    resize() {
        this.camera.resize();
        this.renderer.resize(this.cameraControls);
    }

    update() {
        // this.camera.update(); // Why can this be turned off?
        this.world.update();
        // this.hotspots.update(this.raycaster, this.camera.instance, this.sizes, this.scene, this.resources, this.frustum, this.cameraViewProjectionMatrix);
        // this.renderer.update(); // Why can this be turned off?
        // this.trashbags.update(this.raycaster, this.camera.instance, this.sizes, this.scene, this.resources, this.frustum, this.cameraViewProjectionMatrix);
        // this.trashbags.update(this.resources)
        // this.teleports.update();
        this.lodManager.update(this.cameraControls, this.resources);
    }

    destroy() {
        this.sizes.off('resize'); // Remove resize listeners
        this.time.off('tick'); // Remove tick listeners

        // Traverse the whole scene to dispose of objects
        this.scene.traverse((child) => {
            // Check if it's a mesh
            if (child instanceof THREE.Mesh) {
                child.geometry.dispose();

                for (const key in child.material) {
                    const value = child.material[key];

                    if (value && typeof value.dispose === 'function') {
                        value.dispose();
                    }
                }
            }
        });

        // Dispose of camera controls and renderer
        this.camera.controls.dispose();
        this.renderer.instance.dispose();

        // If using postprocessing, dispose of EffectComposer and any passes
        // this.effectComposer.dispose();

        // if (this.debug.active) {
        //     this.debug.ui.destroy();
        // }
    }
}
